import { AffidavitTemplate, FirebaseTimestamp, Notarization } from '.';
import { EUser } from './user';
import { AdditionalFee, ERate } from './rates';
import { ETemplate } from './templates';
import { DeadlineSettings } from './deadlines';
import { ENotice } from './notice';
import { ERef, StorageReference } from './firebase';
import { EAddress, EPayToAddress } from './address';
import { OrganizationUploadID } from './displaySite';
import { ExternalUploadSettings } from './externalUploads';
import { BuildFormat, SyncFormat } from './integrations/sync';
import { IntegrationUploadSettings, UploadSettingsFTP } from './integrations';
import { PaymentGateway } from './invoices';
import { StripePaymentMethodType } from './stripe';
import { FeeSplit } from './feeSplit';
import { EEditionUploadMethod } from './eedition';
import { OrganizationTypeValue } from '../enums/OrganizationType';
import { ProductUploadSetting } from './upload';
import { PublisherAAC } from './affidavits';
import { ImplementationStatus, Product } from '../enums';
import { CustomNoticeFilingType, FilingType } from './filingType';
import { AdjudicationArea } from './adjudicationArea';
import { ExpressPlacementBilling } from './expressPlacementBilling';
import { PublisherTransferTrigger } from './transfers';
import { IntegrationSettings } from './integrationSettings';
import { NoticeAddOnPricingConfig } from '../pricing/noticeAddOns/types';

// Don't allow organizations to have long names because of potential for spam abuse
export const MAX_ORGANIZATION_NAME_LENGTH = 120;

export type SearchContextSidebarLink = {
  label: string;
  url: string;
};

export type SearchContext = {
  excludeKeywords?: string[];
  state?: {
    label: string;
    value: string;
  };
  paper?:
    | {
        label: string;
        value: string;
      }
    | string[];
  noticeType?:
    | {
        label: string;
        value: string;
      }
    | string[];
  county?:
    | {
        label: string;
        value: string;
      }
    | string[];
  newspaperName?:
    | {
        label: string;
        value: string;
      }
    | string[];
  darkBackground?: string;
  mapTheme?: string;
  mapCenter?: [number, number];
  showGeography?: boolean;
  sidebarText?: string;
  sidebarLink?: SearchContextSidebarLink;
  header?: {
    hide?: boolean;
    label: string;
    button: string;
    link: string;
  };
  maxSearchHistory?: number;
  hidePlaceNoticeLink?: boolean;

  hidePoweredByColumn?: boolean;

  /**
   * Conditionally hides the notice type filter in the UI
   */
  hideNoticeType?: boolean;

  /**
   * When true, hides the location metadata row on search result cards
   */
  hideLocation?: boolean;

  subscriptionSubheader?: string;
  hideAlerts?: boolean;
  filer?: string;

  /**
   * By default we go back one month and end at the current date.
   * The fields below allow overriding this behavior to set custom date ranges.
   * Referenced here: display/server/utils/search.ts
   */
  initialMonthRange?: number;
  showFutureMonths?: number;

  searchId?: string;
  analytics?: string;
  showState?: boolean;
  subscriptionType?: string;
  productId?: string;
  /**
   * When true, notice types defined in `allowedNotices` will be shown on the publisher's display site
   */
  showCustomNoticeTypes?: boolean;
  /**
   * When true, the display site will show both HTML text and a PDF view for each notice
   */
  showComboContent?: boolean;
};

export type CustomIdSettings = {
  source: 'invoice';
};

export type FTPSettings = {
  username: string;
  initialPassword: string;
  reportUrl?: string;
};

export type DistributeSettings = {
  evenly?: boolean;
  finalLineItem?: boolean;
  affidavitFee?: boolean;
  proportionally?: boolean;
};

export type BulkDownloadNoticeGrouping = 'all_liners' | 'liners_by_notice_type';
export type DefaultBulkDownloadSettings = {
  linerFormat: number;
  displayFormat: number;
  grouping: BulkDownloadNoticeGrouping;
  downloadOnlyPaidNotices?: boolean;
  downloadOnlyConfirmedAndPaidDisplays?: boolean;
  bulkFormats?: {
    idml?: boolean;
    rtf?: boolean;
    txt?: boolean;
    docx?: boolean;
  };
};

export type ExpressSettings = {
  acceptsOnlinePayment: boolean | null;
  settingsLastConfirmed: FirebaseTimestamp | null;
  settingsLastConfirmedBy: ERef<EUser> | null;

  publishingDaysConfirmed: boolean | null;
  deadlinesConfirmed: boolean | null;

  /**
   * Some publishers have unique emails for affidavit requests
   */
  affidavitRequestEmail: string | null;
  /**
   * Some publishers have unique emails for invoice requests
   */
  invoiceRequestEmail: string | null;

  /**
   * Indicates whether the publisher provides digital affidavits
   */
  providesDigitalAffidavits: boolean | null;
};

export type BulkDownloadEmail = {
  type: 'email';
  to: string[];
  replyTo: string;
};
export type BulkDownloadFTP = { type: 'ftp'; to: UploadSettingsFTP };
export type BulkDownloadDestination = BulkDownloadFTP | BulkDownloadEmail;
export type BulkDownloadSettings = {
  /** When true, publisher receives their bulk downloads automatically */
  automatic?: boolean;

  /** Time after last deadline to send the download (default: 5) */
  offsetMinutes?: number;

  /** The email or FTP instructions for sending the download */
  destination?: BulkDownloadDestination;

  /** Defaults for settings normally chosen in modal, to be used in automatic download */
  defaultSettings: DefaultBulkDownloadSettings;

  invoiceStatus?: number;
  noLinerImages: boolean;
  noRTF: boolean;
  noTXT: boolean;

  /** @deprecated Used by legacy integrations to export notices via FTP or HTTP */
  ftp?: {
    server: string;
    username?: string;
    password?: string;
    port: number;
    path: string;
    secure: boolean;
    useIntegrationsProxy?: boolean;
    secretKeyRef?: string;
  };
};

type DisableSyncButtonConfig = {
  /**
   * If set, hides the sync button based on the current time relative to the notice's deadline.
   * If set to 'before', the button will be disabled if the current time is before the deadline.
   * If set to 'after', the button will be disabled if the current time is after the deadline.
   */
  basedOnDeadline?: 'before' | 'after' | 'none';
  /**
   * A list of sync statuses for which the sync button should be hidden. If the notice's most recent
   * sync status is in this list, the sync button will be disabled in app.
   */
  basedOnSyncStatus?: number[];
};
export type XMLSyncExportSettings = {
  /**
   * Format determines which scripts to run to export notices and parse responses, if relevant.
   * This is also being used for certain payment gateway configurations.
   */
  format: SyncFormat;

  // Sync Triggers (correspond to events in notice lifecycle)
  syncOnCancel?: boolean;
  syncOnConfirm?: boolean;
  syncOnCustomIdUpdate?: boolean;
  syncOnDeadline?: boolean;
  syncOnEdit?: boolean;
  syncOnInvoiceCreate?: boolean;
  syncOnInvoicePaid?: boolean;
  syncOnInvoicePaymentInitiated?: boolean;
  syncOnInvoiceRefund?: boolean;
  syncOnNoticeCreate?: boolean;
  /**
   * When a notice has requireUpfrontpayment changed from true
   * to false by the publisher, and syncOnUpfrontPaymentWaiver
   * is set to true by the notice's newspaper or its parent,
   * the notice will sync.
   */
  syncOnUpfrontPaymentWaiver?: boolean;

  // Other integration settings
  externalId?: string;
  skipUnpaidPrepaid?: boolean;
  numberOfRequiredSyncs?: number;
  cancelUnpaidPrepaid?: boolean;
  cancelUnpaidPrepaidOnDeadlineSync?: boolean;
  defaultCancellationReason?: string;
  syncPaidPrepaidOnly?: boolean;
  /**
   * @deprecated We previously used this flag to hide the sync button for specific publishers.
   * Now, we use the `disableSyncButton` property to more precisely control when the sync button
   * is disabled.
   */
  hideSyncButton?: boolean;
  /**
   * Configuration used to conditionally disable the in-app Sync Notice button.
   */
  disableSyncButton?: DisableSyncButtonConfig;
  debouncedQueueTimeInSeconds?: number;
  /**
   * For some integrations, we only sync display ads or liner ads, managed by this setting.
   */
  syncSpecific?: 'display' | 'liners';
  /**
   * The firebase storage path to an alternate custom invoice template for billable customers, for
   * use when publishers need to conditionally use an alternate template for specific customers
   */
  billableCustomerCustomInvoice?: string;
  /**
   * For newspapers with an integration that syncs at their deadline, this property adds an optional
   * delay (in minutes) to the time the task will run. This allows newspapers to maintain their
   * existing deadline, while allowing for some buffer to review notices before they sync.
   */
  deadlineDelayTimeInMinutes?: number;
  /**
   * For some integrations, multiple publishers must share a single queue; this settings allows us
   * to specify which organization's queue to use for this paper.
   */
  sharedQueuePublisherId?: string;
};

export type BuildIntegrationExportSettings = {
  buildFormat: BuildFormat;
  orderNumberDigitCount?: number;
  nextOrderNumber?: number;
  buildIntegrationUploadSettings: IntegrationUploadSettings;
};

export type CancelNoticeSettings = {
  allowInitiated?: boolean;
  requireInvoiceAtDeadline?: boolean;
  requireUpfrontPaymentAtDeadline?: boolean;
};

export type MailingAddress = {
  copies?: number;
  description: string;
  isCourthouse?: boolean;
  name: string;
  address: EAddress;
  isNoticeTypeDefault?: boolean;
  courtHouse?: number;
};

/**
 * @type {AffidavitReconciliationSettings} describes the settings we can implement at the
 * organization level to determine how Column is involved in the affidavit processing of this
 * paper. This includes options to manage the affidavits on their behalf
 *
 * @property {boolean} managedByColumn determines whether or not Column is responsible for
 * affidavit processing with this publisher. If true, Column holds the responsibility of ensuring
 * that we create affidavits for all notices that are published
 *
 * @property {boolean} notarizationRequired determines whether or not this published needs notarization
 * for their publication on top of verification of publication.
 *
 * TODO: Some of these properties only make sense on notices! We should probably update the type definition
 * of ARS on notices to include any properties found on orgs & the notice-specific properties (e.g. notarization,
 * overrideNotarizationPreconditions, stateMailCenter)
 */
export type AffidavitReconciliationSettings = {
  affidavitsManagedByColumn: boolean;
  notarizationRequired: boolean;
  uploadMethod: EEditionUploadMethod | 'not-applicable';

  /**
   * @deprecated GCP storage path of the template used for custom affidavits. If empty the paper's
   * existing affidavit template is used.
   */
  managedAffidavitTemplateStoragePath: string;

  managedAffidavitTemplate?: ERef<AffidavitTemplate>;

  // person responsible for signing the affidavits associated with this account
  // TODO: probably delete these?
  /** @deprecated */
  affidavitSigningFirstName?: string;
  /** @deprecated */
  affidavitSigningLastName?: string;
  /** @deprecated */
  affidavitSigningPhone?: string;
  /** @deprecated */
  affidavitSigningEmail?: string;

  // which software vendor does this client use for notarization management
  // if manual, we will sign the affidavits "wet signature" and mail them to the client
  notarizationVendor: 'notarize' | 'manual';

  // specifies a fee for affidavit processing; defaults to 500 ($5) if none is specified
  automatedAffidavitFeeInCents?: number;

  // these fields will only be set on a notice, not a newspaper!
  /** The notarization ref in which this notice will be included */
  notarization?: ERef<Notarization> | null;
  /** Used to initiate notarization for notices that have not yet met all preconditions */
  overrideNotarizationPreconditions?: boolean;

  /**
   * APP-3749: Used to bypass automatic verification on e-edition upload and have the notice
   * go through the manual verification flow.
   * This is currently only intended to be set up on a notice-type level.
   */
  bypassAutomaticVerification?: boolean;

  /**
   * @deprecated flag if enabled allows publishers to verify publication of notices
   * */
  checkboxVerificationEnabled?: boolean;

  // the start date of affidavit reconciliation for this paper
  reconciliationStartDate: FirebaseTimestamp;

  // Base URL for the publisher's e-edition portal used to scrape the e-edition on
  // the publisher's behalf. Sample value is https://tulsaworld.com/eedition
  editionPortalURL?: string;

  /**
   * The value of the mail center enum to which this notice has been assigned
   * for wet-signature notarization
   */
  assignedMailCenter?: number;

  /**
   * Indicates when was the last time a notice was assigned to a Proof transaction/notarization (RON)
   * or to a mail center (wet signature).
   */
  lastAssignedToNotarizationOrMailCenterAt?: FirebaseTimestamp;

  /**
   * Notices for which this property is set to `true` will automatically be assigned
   * to notaries (for digital-signature notarization) or mail centers (for wet-signature
   * notarization) in the same state in which the newspaper is located.
   */
  requiresInStateNotary?: boolean;
  affidavitFeeSplit?: {
    feeSplit: FeeSplit;
    /** affidavitFeeSplit.source is used to indicate at what level the
     * ARS containing a feeSplit originated, when copied down onto a notice-level ARS */
    source: 'notice' | 'customer' | 'noticeType' | 'newspaper';
  } | null;
};

export type NoticeHeightThresholds = {
  maxNoticeHeight: number;
  noticeHeightExceededWarning?: string;
};

export type PayColumnInvoices = {
  enabled: boolean;
  email?: string;
  stripeSubscriptionId?: string;
  /**
   * This is the Stripe customer ID for the publisher.
   * This is used to identify the publisher in Stripe when the publisher is a payor;
   * if present on the org without payColumnInvoices.enabled, it means we are using the
   * Stripe customer ID for the publisher to create app fee invoices that are
   * marked as paid immediately after creation.
   */
  stripeCustomerId?: string;

  /**
   * Stripe customer ID used specifically for application-fee invoices.
   */
  stripeShadowCustomerId?: string;

  /**
   * If true, the publisher will be charged the payment processing fee by withholding
   * this amount from the transfer amount.  The processing fee withheld is determined
   * by the payment gateway used by the publisher.
   *
   * This feature currently only works with Stripe.
   */
  passPaymentProcessingFeeToPublisher?: boolean;
};

export type ApplicationFeeSettings = {
  /**
   * Flag to enable / disable application fee calculations
   * Does not prevent application fees from being deducted on already
   * paid invoices
   */
  enabled: boolean;

  /**
   * Fixed fee in cents to deduct from every transfer
   */
  flatFeeInCents?: number;

  /**
   * Percentage fee to deduct from every transfer
   * This applies to the subtotal of the amount, not the total as per https://columnpbc.atlassian.net/browse/APP-4067?focusedCommentId=88088
   * e.g. 25.5 implies 25.5%
   */
  percentFee?: number;
};

export type DwollaSource = {
  dwollaFundingSource?: string;

  /**
   * Prop to set on the org to initiate final dwolla payouts, but not impact transfer logic
   * (i.e. transfers will continue via Stripe)
   */
  dwollaFundingSourcePayout?: string;
  dwollaCustomer?: string;

  /**
   * Now deprecated as this applies to all organizations.
   * When enabled the organization will receive Dwolla payouts in large
   * batches containing multiple individual payouts.
   * @deprecated
   */
  batchPayoutsEnabled?: boolean;

  /**
   * The number of days to wait before automatically closing and sending
   * any open payouts. The default is 7 calendar days.
   * @deprecated
   */
  batchPayoutsCadenceDays?: number;
};

/**
 * @type {PDFConfigurations} describes the settings we can implement at the
 * organization level to determine how the notice PDF URL generator function
 * formats the downloadable PDF for the notice. This includes options to round
 * the actual height of an ad as well as apply borders depending on the ad type.
 *
 * @property {number} roundAdHeight tells the PDF URL generator
 * function to round the actual ad height of a notice based on a given numeric
 * increment. For example, given roundAdHeight is 0.25 and a notice that is 7.3
 * inches tall, the PDF URL generator would yield a notice that is 7.5 inches.
 *
 * @property {NoticeBorder} applyBorderToLiners tells the PDF
 * URL generator function whether to apply a border to liner ads after adjusting
 * the ad height.
 *
 * @property {NoticeBorder} applyBorderToDisplay tells the PDF
 * URL generator function whether to apply a border to display ads after
 * adjusting the ad height. */
export type PDFConfigurations = {
  roundAdHeight?: number;
  applyBorderToLiners?: boolean;
  applyBorderToDisplay?: boolean;
};

export type InvoiceConfiguration = {
  /**
   * A custom logo we use in place of our own on invoices, pulled from firebase storage.
   */
  logo?: StorageReference;
  /**
   * Information we can use to replace the address shown on invoices generated for a publisher.
   */
  payToAddress?: EPayToAddress;
  /**
   * Information we can use to replace the contact info shown on invoices generated for a publisher.
   */
  contact_info?: {
    email?: string;
    phone?: string;
    url?: string;
  };
  /**
   * A boolean that determines if we include the Order Number (customId) in the invoice PDF
   */
  showOrderNumber?: boolean;
  /**
   * A boolean that determines if we include the Account Number (from notice -> custom -> customer
   * organization) in the invoice PDF
   */
  showAccountNumber?: boolean;

  /**
   * If we should show a line for total column inches or not
   */
  showTotalColumnInches?: boolean;

  /**
   * If we should show number of lines or not
   */
  showNumberOfLines?: boolean;

  /**
   * If we should show number of lines or not
   */
  showColumnWidth?: boolean;

  /**
   * If we should show height or not
   */
  showHeight?: boolean;

  /**
   * If not undefined, show a surcharge warning on the invoice PDF, proof and pay invoice page
   * */
  surchargeWarning?: string;
};

type PercentageBasedPricing = {
  type: 'percent';

  /** Percent of the subtotal that should be charged as a design fee */
  subtotalPct: number;
};

type SizeBasedPricing = {
  type: 'sized';
};

export type AsyncDesignPricing = {
  /** Optional fee caps. The lowest cap applies. */
  cap?: {
    /** Maximum charge IN CENTS per page of design */
    perPage?: number;

    /** Maximum charge IN CENTS for total design */
    total?: number;
  };
} & (PercentageBasedPricing | SizeBasedPricing);

/**
 * Column will provide services relating to order entry, order management, and customer service for a quantity of notices
 */
export type ColumnServicesPricing = {
  /** Contractually agreed upon price paid by the publisher each month for processing notices through Column */
  baseRateInDollars: number;

  /** Contractually agreed upon number of notices the publisher can process through Column before the overage fee is applied */
  baseNoticeVolume: number;

  /** Used to calculate the final overageFee. If the bundle is 100 notices and the publisher processes 51 over the baseNoticeVolume
   *  in the billing month and 49 over the previous month, we take the average (50) and round to the nearest bundle size, in this case 100.
   */
  noticeBundleSize: number;

  /** When calculating the overage fee we take the overageFeeInDollars and multiply by number of times the notice bundle is reached.
   *  In the example above, we average and round to 100 then divide by bundleSize (100 / 100) * overageFeeInDollars = overage fee charged to publisher
   */
  overageFeeInDollars: number;
};

/**
 * Describes unique features of the newspaper's plan as agreed in their contract.
 */
export type PlanSettings = {
  features: {
    /** When true, the organization has column reps staffing */
    columnReps?: boolean;

    /** When true, the organization uses Column's pagination design services for public notice. */
    customPagination?: boolean;

    /** When true, the organization uses Column's pagination design services for obituaries. */
    customPaginationObits?: boolean;

    /** When true, the organization uses Column's pagination design services for classifieds. */
    customPaginationClassifieds?: boolean;

    /** The slack channel ID used to notify pagination reps. For now, this is currently the same channel for all pagination product lines. */
    paginationSlackChannelId?: string;
  };
};

/**
 * @deprecated - Use the instance methods on OrganizationModel to get the
 * appropriate settings based on product and publishingMedium
 * See: ProductPublishingSettings, PublishingSettings
 */
export type ProductLineOrgSettings = {
  /**
   * These deadlines will differ from the standard deadlines in that we don't expect
   * them to impact the subcollection of deadlines and have impacts on syncs as the
   * intent is to have obituaries work exclusively outside of integrations and leverage
   * the bulk download feature.
   */
  deadlines: DeadlineSettings[];
  deadlineOverrides?: Record<string, DeadlineSettings>;
  filingTypes: FilingType[];
};

/**
 * This type contains data for both advertiser organizations and newspapers
 * For newspapers, the following subcollections exist on this record:
 *  - productPublishingSettings
 *  - deadlines
 *
 * TODO: Use type discrimination strategy to isolate advertiser org and newspaper org
 */
export type EOrganization = {
  organizationType: OrganizationTypeValue;
  accountId: string;
  name: string;

  // Templates
  adTemplate?: ERef<ETemplate>;

  /**
   * Fees to deduct from every transfer for this organization
   * Used for Stripe payment processing only
   */
  applicationFeeSettings?: ApplicationFeeSettings;

  /**
   * Default affidavit template for this organization. This can be overriden at the rate, customer or notice level.
   */
  defaultAffidavitTemplate?: ERef<AffidavitTemplate>;

  fullPageTemplate?: string;

  /**
   * Optional additional template that includes all of the notices
   * that are set at two columns
   */
  twoColumnFullPageTemplate?: string;

  signatoryName?: string;
  signatoryRole?: string;
  commissionExpirationDate?: string;
  publicationFrequency?: 'Daily' | 'Weekly';

  // Address
  address: string;
  addressLine2?: string;
  city: string;
  county: string;
  country: number;
  state: number;
  zipCode: string;

  // Contact information
  phone: string;
  email?: string;
  /**
   * TODO: Remove this when we introduce a "Contact" model
   */
  publisherPointOfContact?: string;

  deadlines: DeadlineSettings[] | null;
  deadlineOverrides?: Record<string, DeadlineSettings>;
  cleanVariant?: string;
  displayAdHeader?: boolean;
  displayAdFooter?: boolean;
  postRegistrationComplete: boolean;
  /**
   * @property {string} iana_timezone
   * See https://github.com/moment/moment-timezone/blob/develop/data/packed/latest.json
   * For a list of all valid timezones
   */
  iana_timezone: string;
  defaultLinerRate?: ERef<ERate>;
  defaultDisplayRate?: ERef<ERate>;
  linerBorder?: boolean;
  parent?: ERef<EOrganization> | null;
  icon?: string;
  headerFormatString?: string;
  oneRunHeader?: string;
  oneRunFooter?: string;
  footerFormatString?: string;

  /** @deprecated unused */
  newspaperOfRecord?: string;

  /** @deprecated */
  theme?: any;
  subdomain?: string;

  /**
   * Sales tax expressed as a percentage, always applied to notices, and optionally applied to other products
   * based on `Organization.productTaxSettings`
   */
  taxPct: number;
  productTaxSettings?: Record<Product, boolean> | null;
  searchContext?: SearchContext;
  ftp?: FTPSettings;
  reportUrl?: string;
  lobAddressId?: string;

  /** @deprecated unused */
  types?: any;

  /** @deprecated unused */
  type?: any;

  website?: string;

  /** @deprecated unused */
  pricing?: number;

  /**
   * Maximum number of columns a liner ad can be for this paper.
   */
  linerMaxColumns?: number | null;

  /**
   * Maximum number of columns a display ad can be for this paper.
   */
  displayMaxColumns?: number | null;

  /**
   * Minimum number of columns a liner ad can be for this paper.
   */
  linerMinColumns?: number | null;

  /**
   * Minimum number of columns a display ad can be for this paper.
   */
  displayMinColumns?: number | null;

  lightLogo?: string;
  darkLogo?: string;
  createdBy?: string;
  courthouseId?: number;
  distributeEnoticeFee?: DistributeSettings | null;
  alwaysAllowAffidavitDownload?: boolean;

  stripeId?: string;
  advertiserEditingEnabled?: boolean;
  fee?: number;

  /**
   * @property {boolean} flaggedAsSpam
   * Flag indicating if the organization name has been flagged as spam by AI detection.
   * Used to prevent spam organizations from sending invites.
   */
  flaggedAsSpam?: boolean;

  /**
   * @deprecated Historically, we have used this field to assign an affidavit
   * template, by linking directly to it's storage path. This is now handled
   * by the defaultAffidavitTemplate field.
   */
  customAffidavit?: string;
  customProof?: string;

  /**
   * @property {string} customInvoice is a Handlebars HTML template stored in Firebase Storage used
   * to generate invoices for this publisher. We use it when the publisher requires a degree of
   * customization not possible within our standard invoice template
   */
  customInvoice?: string;

  /**
   * @property {InvoiceConfiguration} invoiceConfiguration is a mapping of fields on our standard
   * invoice template to newspaper-specific configurations.
   */
  invoiceConfiguration?: InvoiceConfiguration;

  allowedNotices?: CustomNoticeFilingType[];
  delimeter?: string;

  noticeAddOnPricing?: NoticeAddOnPricingConfig;

  postInvoiceEdit?: boolean;
  supportEmail?: string;
  customId?: CustomIdSettings;
  bulkDownload?: BulkDownloadSettings;
  xmlExport?: XMLSyncExportSettings;
  buildExport?: BuildIntegrationExportSettings;
  orderExport?: Record<Product, ERef<IntegrationSettings> | null>;
  disableDisplay?: boolean;
  /**
   * This flag will determine whether users can upload multiple files in the placement flow for a complex display ad
   */
  allowMultipleDisplayFileUploads?: boolean;

  /**
   * The default for a newspaper that determines whether public notices require upfront payment
   * absent any overriding settings on the notice, customer, customer org, or notice type.
   * This setting does not apply to obits and classifieds.
   */
  requireUpfrontPayment?: boolean;
  usersRequireUpfrontPayment?: ERef<EUser>[];

  /**
   * Default number of columns for a notice.
   */
  defaultColumns?: number;

  /**
   * Automatically create invoices when a notice is submitted
   */
  autoInvoice?: boolean;

  /**
   * If we have auto-invoicing turned on, we can additionally enable this field to confirm notices post invoice creation
   */
  autoConfirmOnAutoInvoice?: boolean;

  squashDatesInAffidavit?: boolean;
  customAccept?: string;

  /**
   * If true, unblocks additional fee calculation for fixed price zap/typeform notices
   */
  enableFixedPriceAdditionalFees?: boolean;

  /**
   * An array of additional fees to apply at the newspaper-level
   * If a product is not specified within the fee, it will apply globally to both orders and notices
   */
  additionalFees?: AdditionalFee[];

  mailAffidavitsOutsideColumn?: boolean;
  filingFlowSubdomainImage?: string;
  multiPageAffidavits?: boolean;
  circulationSize?: number;
  affidavitDisabled?: boolean;
  currency?: string;
  displayOnlyAds?: boolean;
  sendInvoiceReminders?: boolean;
  enableInvoiceMailing?: boolean;
  editRunDatesAfterInvoicePaid?: boolean;
  dwolla?: DwollaSource;
  dwollaPaymentEnabled?: boolean;

  /**
   * When set to true, notices publish to display sites immediately
   * upon being confirmed.
   */
  convertPublicNoticeOnConfirmation?: boolean;

  /**
   * When 'true' all notices for this organization will be canceled if they are not
   * confirmed by the publisher before their first deadline.
   */
  requireConfirmation?: boolean;

  hyperlinkPaperName?: boolean;

  /** When true, the organization has the IOC option when creating invoices */
  allowInvoiceOutsideColumn?: boolean;

  /**
   * When true, the "require upfront payment" field on the invoice creation screen
   * is always controlled by the "invoice outside column" field.
   * This setting has no effect if allowInvoiceOutsideColumn is not true.
   *
   * Inherits from parent.
   */
  disableControlUpfrontPayment?: boolean;

  shouldAutoGenOrderNums?: boolean | { liners: boolean; display: boolean };
  numberOfOrders?: number;
  orderNumberGeneration?: {
    orderNumPrefix?: string;
    orderNumSuffix?: string;

    /**
     * The minimum number of digits that will be used for each order number.
     *
     * If the order number requires more digits than this number, we do *not* truncate the order number,
     * but instead allow for additional digits to be added.
     *
     * If not set, the default minimum number of digits is 4 (see getNoticeNumberAndCustomIdFromNewspaper).
     */
    orderNumDigitCount?: number;
  };

  customAffidavitFee?: number;

  /**
   * @property {boolean} onePageAffidavit is a feature flag to resize the
   * proof (right side of affidavit) to fit onto the first page, disregarding
   * impact on readability. This flag does NOT currently impact sizing of the
   * affidavit text portion of the affidavit, so if that section is sufficiently
   * long the affidavit could come out to longer than one page.
   */
  onePageAffidavit?: boolean;

  /**
   * @deprecated shouldUseNewPDF was a feature flag that told various functions
   * how to generate PDFs. When true, they used the new puppeteer and pdf-lib
   * based methods; when false, they used a ConvertAPI process. Now, all papers
   * use the puppeteer-based method for document generation.
   */
  shouldUseNewPDF?: boolean;

  /**
   * @deprecated previousCustomInvoice was a place to store the ConverAPI version
   * of a customInvoice template between toggles of the shouldUseNewPDF property,
   * so we could revert without running additional conversions on the document.
   */
  previousCustomInvoice?: string;

  // if we should hide the send affidavits via email button
  hideDigitalAffidavits?: boolean;

  // if we should support the notarization checkbox in battleborn flow
  customNotarizedAffidavit?: string;

  // TODO: Deprecate these fields as they should be at the customer org level
  invoiceOutsideColumn?: boolean;
  internalID?: string;

  // if the paper uses a custom gateway
  gateway?: PaymentGateway;

  /**
   * @property {Record<StripePaymentMethodType, boolean>} restrictPaymentMethods is a map of
   * payment method types to a boolean value that tells our invoice generation functions whether
   * a publisher blocks that payment type (if possible). These options apply donwstream of any
   * gateway-specific (or other general) limitations on payment acceptance, such that they only add
   * restrictions. For example, if `paper_check: false` but a gateway does not support checks, then
   * we will still block check payments. However, if a gateway (and other general configuration)
   * allows check payments, but `paper_check: true`, then invoices will not allow check payment.
   */
  restrictPaymentMethods?: Record<StripePaymentMethodType, boolean>;

  /**
   * @deprecated Formerly used to enable early payouts of ad at ad deadline time. Use `transferTrigger` framework instead.
   */
  releasePaymentOnAdDeadline?: boolean;

  /**
   * When `true`, this property will hide the paper in the publication dropdown for the customer placement flow.
   * Notices can still be placed in a disabled publication.
   */
  disabled?: boolean;

  /**
   * @property {PDFConfigurations} configureNoticePDFs contains the organization
   * level options used by getPDFUrlFromNotice function, which allow us to round
   * the actual height of a notice as well as apply a border around this rounded
   * height PDF.
   */
  configureNoticePDFs?: PDFConfigurations;

  // threshold sizes at which notices should be set to different column widths
  thresholds?: Record<string, number>;

  // require address submission in the placement flow for certain papers
  requireAddressOnFilers?: boolean;

  // require last name to be filled in for customers during placement
  requireLastNameOnFilers?: boolean;

  /**
   * Require phone number to be filled in for customers during placement
   */
  requirePhoneOnFilers?: boolean;

  /**
   * payColumnInvoices is a feature flag. When this flag (payColumnInvoices.enabled)
   * is enabled the organization will be invoiced (via Stripe Subscriptions)
   * for money owed to column such as processing fees and notice refunds.
   *
   * When this is set then the 'stripeSubscriptionId' and 'stripeCustomerId'
   * fields are used to invoice the customer.
   *
   * Note: this flag is inherited from parent org to child org but the child org
   * takes priority.
   */
  payColumnInvoices?: PayColumnInvoices;

  /**
   * @property {boolean} alwaysShowInPlacementFlow will allow a paper to be shown
   * in the newspaper dropdown in the placement flow even if the newspaper does not
   * use Column as an ingestion tool.
   */
  alwaysShowInPlacementFlow?: boolean;

  /**
   * @property {string} nonColumnPlacementSite is a URL string to a newspaper's site
   * where a user can place notices. We use this property
   */
  nonColumnPlacementSite?: string;

  /**
   * @property {ExpressSettings} expressSettings contains metadata about express publication
   */
  expressSettings?: ExpressSettings;

  // column thresholds at which we will switch to a different template
  templateThresholds?: Record<ENotice['columns'], ERef<ETemplate>>;

  /**
   * @property {NoticeHeightThresholds} noticeHeightThresholds allows publishers to configure
   * the boundaries on allowed sizes of notices that are then placed in their papers
   */
  noticeHeightThresholds?: NoticeHeightThresholds;

  /**
   * @property {number} defaultNoticeType sets a default notice type in the
   * placement flow
   */
  defaultNoticeType?: number | null;

  /**
   * @property {string} rssFeedKey is a string used to authenticate a connection to an RSS feed endpoint for a given newspaper.
   */
  rssFeedKey?: string;

  /**
   * @property {boolean} sendInvoiceNotificationsForIOCNotices - when true, this property will send advertisers notifications when a new invoice is created or voided for notices that are invoiced outside Column.
   */
  sendInvoiceNotificationsForIOCNotices?: boolean;

  /**
   * @property {OrganizationUploadID[]} uploadIDs is an array that maps an
   * organization to its state site(s) and corresponding upload ID(s)
   */
  uploadIDs?: OrganizationUploadID[];

  /**
   * @property {AffidavitReconciliationSettings} affidavitReconciliationSettings allows publishers to configure
   * automatic confirmation of notice runs.
   */
  affidavitReconciliationSettings?: AffidavitReconciliationSettings | null;

  /**
   * Automated Affidavit Configuration (AAC) settings that serve as the base
   * settings for all notices with this publisher. Can be overriden by settings
   * on the notice type or customer level.
   *
   * Also includes publisher-specific settings (such as expected upload method)
   * that are not part of the AAC passed down to notices.
   */
  automatedAffidavitConfiguration?: PublisherAAC;

  /**
   * @property {number} session is a counter used to identify syncs via the Brainworks integration,
   * ensuring each has a unique ID. For each sync, we grab this field and increment it.
   */
  session?: number;

  /**
   * @property {number} customAffidavitMaxImageWidth is for overriding the default limit we use to
   * determine if a notice image can fit on the first page of an affidavit (alongside the legal
   * block). It should only be used for publishers that have a custom affidavit with more or less
   * space for the proof image than our standard template. Specify the width in inches (convert from
   * pixels at a 96 pixels per inch)
   */
  customAffidavitMaxImageWidth?: number | null;
  /**
   * @property {boolean} disableACH disables ACH payments on the organization. Parent settings
   * for shohuldDisableACH apply to its subsidiaries unless there is an overriding boolean value for
   * disableACH on the subsidiary.
   */
  disableACH?: boolean;
  /**
   * @property {grayscalePDFDisplayUploads} -- describes whether to apply grayscale formatting to Displays Ads that result from upload of PDFs
   */
  grayscalePDFDisplayUploads?: boolean;

  /**
   * @property {boolean} preferCustomerOnParent is a setting that, when true on
   * a parent organization, ensures that any updates to a customer or customerOrganization for one
   * subsidiary are applied to all subsidiaries (including writes). The main motivation here is to
   * ensure that accurate internalIDs are ready for use in integrations.
   */
  preferCustomerOnParent?: boolean;

  /**
   * @property {ExternalUploadSettings} externalUploadSettings is for storing configurations related
   * to uploading public notices to external state sites.
   */
  externalUploadSettings?: ExternalUploadSettings;

  /**
   * @deprecated replaced by integration features.
   */
  showAccountIDInPlacementFlow?: boolean;

  /**
   * @deprecated replaced by `express` OrganizationStatus.
   */
  isPlacementOnly?: boolean;

  /**
   * @property {boolean} isGovernmentPublisher use to identify county governments that can now publish notices in Florida.
   * For more details on Florida counties who can now place notices, see here:
   * https://www.notion.so/column/Florida-Counties-e0bb6524bfaa4ec0891862be34b990ad
   */
  isGovernmentPublisher?: boolean;

  /**
   * Integration upload settings provide both the format of the upload ("FTP" or "HTTP") as well as
   * the necessary details needed to successfully authorize uploads.
   */
  integrationUploadSettings?: IntegrationUploadSettings;

  /**
   * Product upload settings hold product-specific settings for uploading ads to the newspaper
   * When null, we should defer to the parent settings.
   */
  productUploadSettings?: ProductUploadSetting[] | null;

  /**
   * The current status of the organization, used to distinguish between different newspaper status
   * via OrganizationStatus enum, such as papers still in implementation, those that use Column only
   * to upload to state sites, and those that accept notices via ColumnExpress.
   */
  organizationStatus: number;

  /**
   * This status only applies to newspapers!
   */
  obituaryImplementationStatus?: ImplementationStatus;

  /**
   * This status only applies to newspapers!
   */
  classifiedImplementationStatus?: ImplementationStatus;

  /**
   * @deprecated - See ProductLineOrgSettings
   * Contains all settings that are specific to obituary ad types
   */
  obituarySettings?: ProductLineOrgSettings;

  /**
   * @deprecated - See ProductLineOrgSettings
   * Contains all settings that are specific to classified ad types
   */
  classifiedSettings?: ProductLineOrgSettings;

  /**
   *  When this is true on the newspaper or on the notice type, we allow inline images
   * in liner ads.
   */
  allowImagesInLiners?: boolean;

  /*
   * Used with advertiser organization to show only relative organizations in advertiser registration search
   */
  showInSearch?: boolean;

  /**
   * Effectively disables payment processing flows for a new organization until we're ready to launch
   * Mainly setup for Lee via IT-4946
   */
  blockNonIOCInvoices?: boolean;

  /**
   * List of custom roles available to this organization in addition to the base roles (Admin/User)
   */
  allowedRoles?: number[];

  /**
   * When true, hides newspaper dimensions from generated affidavits
   * This setting only applies to newspapers
   */
  hideNewspaperDimensionsOnAffidavits?: boolean;

  /**
   * Determines if and how Column charges for async design work. Can be inherited
   * from the parent.
   */
  asyncDesignPricing?: AsyncDesignPricing;

  /**
   * Determines how we bill a demand-side partner for Express placement.
   * Inherited from parent organization if present and not set on the child.
   */
  expressPlacementBilling?: ExpressPlacementBilling;

  /**
   * @deprecated see noticeHeaders.enabled
   */
  customTopHeaderStyle?: boolean;

  noticeHeaders?: {
    /**
     * When true, apply the custom header styling of template on the notice header text added into the Header field.
     * This setting can enable/disable on the Global Template settings and only available if the template includes header styles.
     */
    enabled?: boolean;

    /**
     * When true (and enabled is true) the placement flow requires the header field.
     */
    required?: boolean;
  };

  /**
   * If enabled, customers will not see the option to submit without formatting unless their files are too large to process in a timely fashion.
   */
  restrictSubmitWithoutFormatting?: boolean;

  cancelNoticeSettings?: CancelNoticeSettings;

  /**
   * Determines if and how Column charges for services relating to order entry, order management, and customer service.
   * Can be inherited from the parent.
   */
  columnServicesPricing?: ColumnServicesPricing;

  /**
   * Lists settings/features of the publisher's plan that affect app behavior.
   */
  planSettings?: PlanSettings;

  /**
   * List of state/county/place objects to flag which geographic areas the publisher serves.
   */
  adjudicationAreas?: ERef<AdjudicationArea>[];

  /**
   * When to trigger the transfer of funds to the newspaper
   * Currently applies only to notices
   */
  transferTrigger?: PublisherTransferTrigger;

  /**
   * If enabled, customers will be blocked from placing notices until they are approved by a legal rep.
   */
  requireApprovalToPlaceOrders?: boolean;
};

export type OrgContextState =
  | ({ id: string } & Pick<
      EOrganization,
      | 'name'
      | 'state'
      | 'organizationType'
      | 'subdomain'
      | 'allowedNotices'
      | 'filingFlowSubdomainImage'
      | 'searchContext'
      | 'subdomain'
      | 'darkLogo'
    >)
  | null;
